import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import FeatureHeader from './FeatureHeader';

export default function DangerZone({ onBack }) {
  const [showModal, setShowModal] = useState(false);
  const [toast, setToast] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const navigate = useNavigate();
  const { user, logout } = useAuth() || {};

  const showToast = (message) => {
    setToast(message);
    setTimeout(() => setToast(''), 3000);
  };

  const handleDelete = async () => {
    setShowModal(false);
    const storedUser = (() => {
      try {
        return JSON.parse(localStorage.getItem('user') || '{}');
      } catch {
        return {};
      }
    })();
    const userId = user?._id || user?.id || localStorage.getItem('userId') || storedUser?._id || storedUser?.id;

    if (!userId) {
      showToast('Unable to delete account because no signed-in user was found.');
      return;
    }

    try {
      setIsDeleting(true);
      await api.delete('/api/delete-account', {
        params: { userId },
        data: { userId },
      });
      showToast('Account deleted successfully. Redirecting to sign in...');

      if (typeof logout === 'function') {
        await logout();
      } else {
        localStorage.clear();
      }

      setTimeout(() => {
        navigate('/signin', { replace: true });
      }, 1200);
    } catch (err) {

      showToast(err?.response?.data?.message || 'Failed to delete account. Please try again.');
      setIsDeleting(false);
    }
  };

  return (
    <div style={pageStyle}>
      <FeatureHeader title="Danger Zone" onBack={onBack} />

      <div style={warningCard}>
        <AlertTriangle size={24} style={{ color: 'red' }} />
        <div>
          <div style={{ fontWeight: '600', fontSize: '0.95rem' }}>Delete Account</div>
          <div style={{ fontSize: '0.75rem', color: '#666' }}>
            This action is irreversible. All your data will be permanently removed.
          </div>
        </div>
      </div>

      <button
        onClick={() => setShowModal(true)}
        disabled={isDeleting}
        style={{ ...deleteBtnStyle, opacity: isDeleting ? 0.65 : 1, cursor: isDeleting ? 'not-allowed' : 'pointer' }}
      >
        {isDeleting ? 'Deleting Account...' : 'Delete My Account'}
      </button>

      {toast && <div style={toastStyle}>{toast}</div>}

      {showModal && (
        <div style={modalOverlay}>
          <div style={modalCard}>
            <h2 style={{ fontSize: '1rem', marginBottom: 8 }}>Confirm Deletion</h2>
            <p style={{ fontSize: '0.85rem', marginBottom: 16 }}>
              Are you sure you want to permanently delete your account?
            </p>
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                style={{ ...confirmBtnStyle, opacity: isDeleting ? 0.65 : 1, cursor: isDeleting ? 'not-allowed' : 'pointer' }}
              >
                {isDeleting ? 'Deleting...' : 'Yes, Delete'}
              </button>
              <button onClick={() => setShowModal(false)} disabled={isDeleting} style={cancelBtnStyle}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const pageStyle = {
  minHeight: '100vh',
  padding: '24px 16px',
  background: 'var(--bg)',
  fontFamily: 'sans-serif',
  color: 'var(--on-bg)',
};

const warningCard = {
  background: 'var(--surface)',
  borderRadius: 12,
  padding: 16,
  marginBottom: 24,
  boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
  display: 'flex',
  alignItems: 'center',
  gap: 12,
};

const deleteBtnStyle = {
  padding: '10px 16px',
  background: 'red',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  fontSize: '0.9rem',
  fontWeight: '500',
  width: '100%',
};

const toastStyle = {
  position: 'fixed',
  bottom: '20px',
  left: '50%',
  transform: 'translateX(-50%)',
  background: 'var(--accent)',
  color: '#fff',
  padding: '10px 16px',
  borderRadius: '8px',
  fontSize: '0.85rem',
  zIndex: 9999,
  boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
};

const modalOverlay = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0,0,0,0.3)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 999,
};

const modalCard = {
  background: 'var(--surface)',
  padding: 24,
  borderRadius: 12,
  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
  width: '90%',
  maxWidth: 320,
};

const confirmBtnStyle = {
  padding: '10px 16px',
  background: 'red',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  fontSize: '0.85rem',
  fontWeight: '500',
};

const cancelBtnStyle = {
  padding: '10px 16px',
  background: '#eee',
  color: '#333',
  border: 'none',
  borderRadius: 8,
  fontSize: '0.85rem',
  fontWeight: '500',
};
